import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  IndianRupee, GraduationCap, Users, LifeBuoy,
  TrendingUp, Clock, CheckCircle2, XCircle, Activity,
  Map, Smartphone, Zap, AlertCircle, FileWarning, Timer, CalendarDays, Building2, Globe2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend
} from 'recharts';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';

// ── helpers ──────────────────────────────────────────────────────────
const defaultStats = {
  totalRevenue: 0,
  totalAdmissions: 0,
  totalStudents: 0,
  pendingCount: 0,
  activeTickets: 0,
  revenueChart: [],
  admissionFunnel: [],
  paymentBreakdown: [],
  collegeAdmissions: [],
  courseSeatChart: [],
  abroadAdmissions: { total: 0, countries: [] },
  recentActivity: [],
  admissionIntelligence: {
    avgCompletionTime: 0,
    failedPayments: 0,
    docsPending: 0,
    topStates: [],
    peakTimings: [],
    deviceUsage: [],
    osUsage: [],
  },
};

const normalizeStats = (value = {}) => ({
  ...defaultStats,
  ...value,
  revenueChart: value.revenueChart || [],
  admissionFunnel: value.admissionFunnel || [],
  paymentBreakdown: value.paymentBreakdown || [],
  collegeAdmissions: value.collegeAdmissions || [],
  courseSeatChart: value.courseSeatChart || [],
  abroadAdmissions: {
    total: Number(value.abroadAdmissions?.total || 0),
    countries: value.abroadAdmissions?.countries || [],
  },
  recentActivity: value.recentActivity || [],
  admissionIntelligence: {
    ...defaultStats.admissionIntelligence,
    ...(value.admissionIntelligence || {}),
    topStates: value.admissionIntelligence?.topStates || [],
    peakTimings: value.admissionIntelligence?.peakTimings || [],
    deviceUsage: value.admissionIntelligence?.deviceUsage || [],
    osUsage: value.admissionIntelligence?.osUsage || [],
  },
});

const statusBadge = (status, paymentStatus) => {
  if (paymentStatus === 'completed' && status === 'confirmed')
    return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Confirmed</span>;
  if (status === 'cancelled')
    return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Cancelled</span>;
  if (paymentStatus === 'pending')
    return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Pending</span>;
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{status}</span>;
};

const timeAgo = (date, currentTime = Date.now()) => {
  const secs = Math.floor((currentTime - new Date(date)) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const CustomTooltipRevenue = ({ active, payload, label, revenueLabel = 'Platform fee' }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-slate-100 rounded-xl shadow-lg p-3 text-sm">
        <p className="font-bold text-slate-700 mb-1">{label}</p>
        <p className="text-emerald-600 font-semibold">{revenueLabel}: ₹{payload[0]?.value?.toLocaleString('en-IN')}</p>
        <p className="text-indigo-600 font-semibold">Admissions: {payload[1]?.value}</p>
      </div>
    );
  }
  return null;
};

const formatDateInput = (date) => {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().slice(0, 10);
};
const datePresets = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'custom', label: 'Custom' },
];
const presetRange = (preset, customFrom, customTo) => {
  if (preset === 'all') return {};
  if (preset === 'custom') return customFrom ? { dateFrom: customFrom, dateTo: customTo || customFrom } : {};
  const today = new Date();
  const end = new Date(today);
  const start = new Date(today);
  if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  }
  if (preset === '7d') start.setDate(start.getDate() - 6);
  if (preset === '30d') start.setDate(start.getDate() - 29);
  return { dateFrom: formatDateInput(start), dateTo: formatDateInput(end) };
};

// ── Main Component ────────────────────────────────────────────────────
export default function DashboardOverview({ scope = 'admin' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
	  const isInstitutionScope = scope === 'institution';
	  const showAbroadAdmissions = isInstitutionScope || user?.role === 'cluster_admin';
  const isCourseFeeScope = isInstitutionScope || user?.role === 'cluster_admin';
  const apiBase = isInstitutionScope ? '/api/institution-admin' : '/api/admin';
  const routeBase = isInstitutionScope ? '/institution' : '/admin';
  const revenueLabel = isCourseFeeScope ? 'Course fee' : 'Platform fee';
  const revenueTitle = isCourseFeeScope ? 'Course Fee Revenue' : 'Platform Fee Revenue';
  const activityFeeKey = isCourseFeeScope ? 'courseFee' : 'platformFee';
  const canViewRevenueAnalytics = hasPermission(user, PERMISSIONS.DASHBOARD_REVENUE_ANALYTICS);
  const canViewAdmissionFunnel = hasPermission(user, PERMISSIONS.DASHBOARD_ADMISSION_FUNNEL);
  const canViewCollegeAnalytics = hasPermission(user, PERMISSIONS.DASHBOARD_COLLEGE_ANALYTICS);
  const canViewCourseAnalytics = hasPermission(user, PERMISSIONS.DASHBOARD_COURSE_ANALYTICS);
  const canViewAdmissionIntelligence = hasPermission(user, PERMISSIONS.DASHBOARD_ADMISSION_INTELLIGENCE);
  const canViewGeographicOrigin = hasPermission(user, PERMISSIONS.DASHBOARD_GEOGRAPHIC_ORIGIN);
  const canViewPeakTimings = hasPermission(user, PERMISSIONS.DASHBOARD_PEAK_TIMINGS);
  const canViewDeviceUsage = hasPermission(user, PERMISSIONS.DASHBOARD_DEVICE_USAGE);
  const canViewActivityFeed = hasPermission(user, PERMISSIONS.DASHBOARD_ACTIVITY_FEED);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
  const [clusters, setClusters] = useState([]);
  const [selectedClusterId, setSelectedClusterId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState(formatDateInput(new Date()));
  const [customTo, setCustomTo] = useState(formatDateInput(new Date()));

  useEffect(() => {
    const fetchFilters = async () => {
      if (isInstitutionScope || !hasPermission(user, PERMISSIONS.INSTITUTIONS_VIEW)) return;
      try {
        const [instsRes, clustersRes] = await Promise.all([
          axios.get(`${apiBase}/institutions?export=true`),
          axios.get(`${apiBase}/clusters`)
        ]);
        setInstitutions(instsRes.data?.items || instsRes.data || []);
        const clusterItems = clustersRes.data || [];
        setClusters(clusterItems);
        if (user?.role === 'cluster_admin') {
          const cluster = clusterItems.find((item) => (item._id || item.id) === user.managedClusterId);
          setScopeName(cluster?.name || user?.managedCluster?.name || '');
        }
      } catch {
        toast.error('Failed to load filters');
      }
    };
    fetchFilters();
  }, [apiBase, isInstitutionScope, user]);

  useEffect(() => {
    if (!isInstitutionScope) return;
    const fetchInstitutionName = async () => {
      try {
        const res = await axios.get(`${apiBase}/me`);
        const institution = res.data?.institution || {};
        setScopeName(institution.displayName || institution.collegeName || '');
      } catch {
        setScopeName(user?.managedInstitution?.displayName || user?.managedInstitution?.collegeName || '');
      }
    };
    fetchInstitutionName();
  }, [apiBase, isInstitutionScope, user]);

  const fetchStats = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      if (silent) setRefreshing(true);
      const params = {
        ...presetRange(datePreset, customFrom, customTo),
        ...(selectedInstitutionId ? { institutionId: selectedInstitutionId } : {}),
        ...(selectedClusterId ? { clusterId: selectedClusterId } : {}),
      };
      const res = await axios.get(`${apiBase}/stats`, { params });
      setStats(normalizeStats(res.data));
      setLastUpdated(new Date());
    } catch {
      if (!silent) toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBase, datePreset, customFrom, customTo, selectedInstitutionId, selectedClusterId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') fetchStats({ silent: true });
    };
    const intervalId = window.setInterval(refresh, 10000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchStats]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  if (loading) return (
    <div className="h-[80vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!stats) return null;

  const activeDateParams = {
    ...presetRange(datePreset, customFrom, customTo),
    ...(selectedInstitutionId ? { institutionId: selectedInstitutionId } : {}),
    ...(selectedClusterId ? { clusterId: selectedClusterId } : {}),
  };
  const buildLink = (path, params = {}) => {
    const query = new URLSearchParams({ ...activeDateParams, ...params });
    const qs = query.toString();
    return qs ? `${path}?${qs}` : path;
  };
  const statCards = [
    { permission: PERMISSIONS.STATS_REVENUE, title: revenueTitle, value: `₹${stats.totalRevenue.toLocaleString('en-IN')}`, icon: <IndianRupee size={22} />, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', to: buildLink(`${routeBase}/admissions`, { payment: 'completed' }) },
    { permission: PERMISSIONS.STATS_ADMISSIONS, title: 'Paid Admissions', value: stats.totalAdmissions, icon: <GraduationCap size={22} />, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100', to: buildLink(`${routeBase}/admissions`, { payment: 'completed' }) },
    { permission: PERMISSIONS.STATS_STUDENTS, title: 'Total Students', value: stats.totalStudents, icon: <Users size={22} />, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', to: isInstitutionScope ? buildLink(`${routeBase}/admissions`) : buildLink('/admin/students', { role: 'student' }) },
    { permission: PERMISSIONS.STATS_PAYMENTS, title: 'Pending Payments', value: stats.pendingCount, icon: <Clock size={22} />, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', to: buildLink(`${routeBase}/admissions`, { payment: 'pending' }) },
    ...(user?.role === 'admin' ? [{ permission: PERMISSIONS.STATS_TICKETS, title: 'Active Tickets', value: stats.activeTickets, icon: <LifeBuoy size={22} />, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', to: buildLink('/admin/support', { status: 'open' }) }] : []),
  ].filter((card) => hasPermission(user, card.permission));

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur border-b border-slate-200 -mx-8 px-8 py-4">
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-800">Dashboard</h1>
            {scopeName && <p className="text-sm font-semibold text-slate-500 mt-1">{scopeName}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isInstitutionScope && hasPermission(user, PERMISSIONS.INSTITUTIONS_VIEW) && (
              <>
                {user?.role === 'admin' && (
                  <>
                    <div className="flex items-center gap-1 text-xs font-bold text-slate-400 uppercase mr-1">
                      <Building2 size={14} /> Cluster
                    </div>
                    <select
                      value={selectedClusterId}
                      onChange={(e) => {
                        setSelectedClusterId(e.target.value);
                        setSelectedInstitutionId('');
                      }}
                      className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200 max-w-[200px]"
                    >
                      <option value="">All Clusters</option>
                      {clusters.map((cluster) => (
                        <option key={cluster._id || cluster.id} value={cluster._id || cluster.id}>
                          {cluster.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {['admin', 'cluster_admin'].includes(user?.role) && (
                  <>
                    <div className="flex items-center gap-1 text-xs font-bold text-slate-400 uppercase mr-1">
                      <Building2 size={14} /> Institution
                    </div>
                    <select
                      value={selectedInstitutionId}
                      onChange={(e) => setSelectedInstitutionId(e.target.value)}
                      className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200 max-w-[240px]"
                    >
                      <option value="">All Institutions</option>
	                      {institutions
	                        .filter(inst => {
	                          if (user?.role === 'cluster_admin') {
	                            return true;
	                          }
	                          return !selectedClusterId
                              || inst.clusterId === selectedClusterId
                              || inst.cluster?.id === selectedClusterId
                              || inst.cluster?._id === selectedClusterId
                              || (inst.clusters || []).some((cluster) => (
                                (cluster.id || cluster._id) === selectedClusterId
                              ));
	                        })
                        .map((institution) => (
                          <option key={institution._id || institution.id} value={institution._id || institution.id}>
                            {institution.displayName || institution.collegeName}
                          </option>
                        ))}
                    </select>
                  </>
                )}
              </>
            )}
            <div className="flex items-center gap-1 text-xs font-bold text-slate-400 uppercase mr-1">
              <CalendarDays size={14} /> Date
            </div>
            {datePresets.map((preset) => (
              <button
                key={preset.key}
                onClick={() => setDatePreset(preset.key)}
                className={`px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${datePreset === preset.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              >
                {preset.label}
              </button>
            ))}
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200" />
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-200 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((card, idx) => (
          <button key={idx} onClick={() => navigate(card.to)} className={`bg-white rounded-2xl p-5 shadow-sm border ${card.border} flex flex-col gap-3 text-left hover:-translate-y-0.5 hover:shadow-md transition-all`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.bg} ${card.color}`}>
              {card.icon}
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{card.title}</p>
              <h3 className="text-2xl font-black text-slate-800 mt-0.5">{card.value}</h3>
            </div>
          </button>
        ))}
      </div>

      {(canViewRevenueAnalytics || canViewAdmissionFunnel) && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Revenue & Admissions 14-day Area Chart */}
        {canViewRevenueAnalytics && <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><TrendingUp size={18} className="text-emerald-500" /> Revenue & Payment Analytics</h2>
              <p className="text-xs text-slate-500 mt-0.5">Daily {revenueLabel.toLowerCase()} and paid admission count</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.revenueChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAdmissions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={8} />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dx={-4} tickFormatter={v => `₹${v}`} />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dx={4} />
                <Tooltip content={<CustomTooltipRevenue revenueLabel={revenueLabel} />} />
                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorRevenue)" />
                <Area yAxisId="right" type="monotone" dataKey="admissions" stroke="#6366f1" strokeWidth={2.5} fillOpacity={1} fill="url(#colorAdmissions)" strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-3 justify-center">
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-1.5 rounded bg-emerald-500 inline-block"></span>{revenueLabel}</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-1.5 rounded bg-indigo-500 inline-block"></span>Admissions</div>
          </div>
        </div>}

        {/* Admission Funnel */}
        {canViewAdmissionFunnel && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><Activity size={18} className="text-indigo-500" /> Admission Funnel</h2>
          <p className="text-xs text-slate-500 mb-5">Student lifecycle from registration to confirmation</p>
          <div className="space-y-3">
            {stats.admissionFunnel.map((item, i, arr) => {
              const maxCount = Math.max(...arr.map(a => a.count)) || 1;
              const pct = Math.round((item.count / maxCount) * 100);
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">{item.stage}</span>
                      {i > 0 && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold">
                          {arr[i-1].count > 0 ? Math.round((item.count / arr[i-1].count) * 100) : 0}% conversion
                        </span>
                      )}
                    </div>
                    <span className="font-bold text-slate-800">{item.count}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: item.fill }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Payment Pie */}
          <div className="mt-6">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Payment Status Split</p>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.paymentBreakdown} cx="50%" cy="50%" innerRadius={35} outerRadius={58} paddingAngle={3} dataKey="value">
                    {stats.paymentBreakdown.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val, name) => [val, name]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>}
      </div>}

      {/* ── Row 2: College Bar + Course Seats Bar ── */}
      {(canViewCollegeAnalytics || canViewCourseAnalytics) && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* College-wise Admissions Bar */}
        {canViewCollegeAnalytics && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
            <GraduationCap size={18} className="text-indigo-500" /> Institution-wise Admission Analytics
          </h2>
          <p className="text-xs text-slate-500 mb-5">Confirmed admissions per institution</p>
          {stats.collegeAdmissions.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No admission data yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.collegeAdmissions} margin={{ top: 5, right: 10, left: 0, bottom: 30 }} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="short"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#475569', fontSize: 11, fontWeight: 600 }}
                    interval={0}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: '#f8fafc' }}
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white border border-slate-100 rounded-xl shadow-lg p-3 text-sm max-w-[220px]">
                            <p className="font-bold text-slate-800 mb-1 leading-snug">{d.college}</p>
                            <p className="text-indigo-600 font-semibold">{d.count} Admissions</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={55}>
                    {stats.collegeAdmissions.map((_, i) => (
                      <Cell key={i} fill={`hsl(${240 + i * 18}, 70%, ${55 + i * 3}%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>}

        {/* Course-wise Seat Availability Bar */}
        {canViewCourseAnalytics && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
            <TrendingUp size={18} className="text-indigo-500" /> Top Performing Courses
          </h2>
          <p className="text-xs text-slate-500 mb-5">Highest enrolled programs (filled vs available seats)</p>
          {stats.courseSeatChart.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-slate-400 text-sm">No course seat data yet</div>
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.courseSeatChart} margin={{ top: 0, right: 10, left: 0, bottom: 0 }} barCategoryGap="15%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="short" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10 }} angle={-20} textAnchor="end" height={45} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }} 
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white border border-slate-100 rounded-xl shadow-lg p-3 text-sm max-w-[240px]">
                            <p className="font-bold text-slate-800 mb-2 leading-snug">{d.name}</p>
                            <div className="flex flex-col gap-1">
                              <p className="text-indigo-600 font-semibold flex items-center justify-between"><span>Filled Seats:</span> <span>{d.filled}</span></p>
                              <p className="text-emerald-500 font-semibold flex items-center justify-between"><span>Available:</span> <span>{d.available}</span></p>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="filled" name="Filled" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={35} />
                  <Bar dataKey="available" name="Available" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={35} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>}
      </div>}

      {/* ── Row 3: Admission Intelligence Section ── */}
      {(canViewAdmissionIntelligence || canViewGeographicOrigin || canViewPeakTimings || canViewDeviceUsage) && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Intelligence Cards */}
        {canViewAdmissionIntelligence && <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><Timer size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Avg Completion</p>
              <h3 className="text-lg font-black text-slate-800">{stats.admissionIntelligence.avgCompletionTime} mins</h3>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><AlertCircle size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Payment Failures</p>
              <h3 className="text-lg font-black text-slate-800">{stats.admissionIntelligence.failedPayments}</h3>
            </div>
          </div>
          <div 
            className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => navigate(buildLink('/admin/admissions', { payment: 'completed', status: 'pending' }))}
          >
            <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center"><FileWarning size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Docs Pending</p>
              <h3 className="text-lg font-black text-slate-800">{stats.admissionIntelligence.docsPending}</h3>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Drop-off Rate</p>
              <h3 className="text-lg font-black text-slate-800">
                {stats.admissionFunnel[1].count > 0 ? Math.max(0, 100 - Math.round((stats.admissionFunnel[stats.admissionFunnel.length-1].count / stats.admissionFunnel[1].count) * 100)) : 0}%
              </h3>
            </div>
          </div>
        </div>}

        {/* State/City Heatmap */}
	        {canViewGeographicOrigin && <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><Map size={18} className="text-indigo-500" /> Geographic Origin</h2>
          <p className="text-xs text-slate-500 mb-5">Top applying states/cities</p>
          {stats.admissionIntelligence.topStates.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-slate-400 text-sm">No geographic data</div>
          ) : (
            <div className="space-y-4 mt-2">
              {stats.admissionIntelligence.topStates.map((state, i) => {
                const max = stats.admissionIntelligence.topStates[0].value;
                return (
                  <div 
                    key={i} 
                    className="flex items-center gap-3 text-sm cursor-pointer hover:bg-slate-50 p-1.5 -mx-1.5 rounded-lg transition-colors group"
                    onClick={() => navigate(buildLink('/admin/admissions', { district: state.name }))}
                  >
                    <span className="w-24 truncate font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">{state.name}</span>
                    <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${(state.value / max) * 100}%` }}></div>
                    </div>
                    <span className="font-bold text-slate-600 w-8 text-right">{state.value}</span>
                  </div>
                );
              })}
            </div>
          )}
	        </div>}

	        {showAbroadAdmissions && canViewGeographicOrigin && (
	          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
	            <div className="flex items-start justify-between gap-4 mb-5">
	              <div>
	                <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
	                  <Globe2 size={18} className="text-sky-500" /> Abroad Admissions
	                </h2>
	                <p className="text-xs text-slate-500">Paid admissions received from outside India</p>
	              </div>
	              <span className="rounded-xl bg-sky-50 px-3 py-2 text-xl font-black text-sky-700">
	                {stats.abroadAdmissions.total}
	              </span>
	            </div>
	            {stats.abroadAdmissions.countries.length === 0 ? (
	              <div className="h-32 flex items-center justify-center text-slate-400 text-sm">No abroad admissions yet</div>
	            ) : (
	              <div className="space-y-4">
	                {stats.abroadAdmissions.countries.slice(0, 8).map((item) => {
	                  const max = stats.abroadAdmissions.countries[0].count || 1;
	                  return (
	                    <div key={item.country} className="flex items-center gap-3 text-sm">
	                      <span className="w-28 truncate font-medium text-slate-700">{item.country}</span>
	                      <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
	                        <div className="bg-sky-500 h-2 rounded-full" style={{ width: `${(item.count / max) * 100}%` }} />
	                      </div>
	                      <span className="font-bold text-slate-600 w-8 text-right">{item.count}</span>
	                    </div>
	                  );
	                })}
	              </div>
	            )}
	          </div>
	        )}

        {/* Peak Timings & Device Usage */}
        {canViewPeakTimings && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><Zap size={18} className="text-amber-500" /> Peak Timings</h2>
          <p className="text-xs text-slate-500 mb-5">Most active application hours</p>
          {stats.admissionIntelligence.peakTimings.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-slate-400 text-sm">No timing data</div>
          ) : (
            <div className="flex flex-col gap-3">
              {stats.admissionIntelligence.peakTimings.map((time, i) => (
                <div key={i} className="flex items-center justify-between border-b border-slate-50 pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-[10px]">{i+1}</div>
                    <span className="text-sm font-semibold text-slate-700">{time.time}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-md">{time.count} apps</span>
                </div>
              ))}
            </div>
          )}
        </div>}

        {canViewDeviceUsage && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><Smartphone size={18} className="text-emerald-500" /> Device Usage</h2>
          <p className="text-xs text-slate-500 mb-5">Mobile, desktop and OS usage</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.admissionIntelligence.deviceUsage} cx="50%" cy="50%" innerRadius={25} outerRadius={45} paddingAngle={2} dataKey="value">
                  {stats.admissionIntelligence.deviceUsage.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => [`${val}%`, 'Usage']} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {stats.admissionIntelligence.osUsage.length === 0 ? (
              <p className="text-center text-slate-400 text-sm">No OS data</p>
            ) : stats.admissionIntelligence.osUsage.slice(0, 5).map((item) => (
              <div key={item.name} className="flex items-center gap-3 text-sm">
                <span className="w-20 truncate font-semibold text-slate-600">{item.name}</span>
                <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div className="h-2 rounded-full" style={{ width: `${item.value}%`, background: item.fill }} />
                </div>
                <span className="w-12 text-right text-xs font-bold text-slate-500">{item.count}</span>
              </div>
            ))}
          </div>
        </div>}
      </div>}

      {/* ── Row 4: Real-Time Activity Feed ── */}
      {canViewActivityFeed && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              Real-Time Admission Activity Feed
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Most recent 10 applications across all institutions</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-600">
            <span className={`w-2 h-2 rounded-full bg-emerald-500 ${refreshing ? 'animate-pulse' : ''}`} />
            Live{lastUpdated ? ` · updated ${timeAgo(lastUpdated, now)}` : ''}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Application</th>
                <th className="text-left pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Student</th>
                <th className="text-left pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Institution</th>
                <th className="text-left pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Program</th>
                <th className="text-right pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Fee</th>
                <th className="text-center pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-right pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentActivity.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">No activity yet</td></tr>
              ) : (
                stats.recentActivity.map((item) => (
                  <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 font-mono text-xs font-bold text-indigo-600">{item.applicationId || '—'}</td>
                    <td className="py-3 font-medium text-slate-700">{item.studentName}</td>
                    <td className="py-3 text-slate-500 max-w-[140px] truncate">{item.college}</td>
                    <td className="py-3 text-slate-500 max-w-[120px] truncate">{item.program}</td>
                    <td className="py-3 text-right font-semibold text-slate-800">₹{(item[activityFeeKey] || 0).toLocaleString('en-IN')}</td>
                    <td className="py-3 text-center">{statusBadge(item.status, item.paymentStatus)}</td>
                    <td className="py-3 text-right text-xs text-slate-400">{timeAgo(item.createdAt, now)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>}

    </div>
  );
}
